export type ResearchItem = {
  title: string
  field: string
  status: 'Approved use' | 'Cleared device' | 'Investigational'
  date: string
  summary: string
  caution: string
  sources: { label: string; url: string }[]
}

export const researchItems: ResearchItem[] = [
  {
    title: 'Robotic-assisted surgery: precise tools under surgeon control',
    field: 'Surgical technology',
    status: 'Cleared device',
    date: 'Reviewed October 2026',
    summary: 'Computer-assisted systems such as the da Vinci platform let a trained surgeon control a magnified camera and articulated instruments during selected minimally invasive procedures. The system does not decide or operate autonomously.',
    caution: 'Suitability and outcomes depend on the exact operation, patient, team experience and alternatives. Potential minimally invasive benefits are not guaranteed, and FDA device clearance is not proof of improved cancer survival or recurrence outcomes.',
    sources: [
      { label: 'FDA: computer-assisted surgery', url: 'https://www.fda.gov/medical-devices/surgery-devices/computer-assisted-surgical-systems' },
      { label: 'Intuitive: da Vinci systems', url: 'https://www.intuitive.com/en-us/products-and-services/da-vinci' },
      { label: 'Systematic review of RCTs', url: 'https://pubmed.ncbi.nlm.nih.gov/37442833/' },
    ],
  },
  {
    title: 'CRISPR editing for inherited blood disorders',
    field: 'Gene therapy',
    status: 'Approved use',
    date: 'Updated July 2026',
    summary: 'CASGEVY edits a patient’s own blood-forming stem cells outside the body. The US FDA-approved uses include certain patients with recurrent vaso-occlusive sickle-cell disease or transfusion-dependent beta-thalassaemia.',
    caution: 'This is highly specialised, transplant-level care with long-term follow-up. Approval, eligibility and access differ by country.',
    sources: [
      { label: 'FDA: CASGEVY', url: 'https://www.fda.gov/vaccines-blood-biologics/casgevy' },
      { label: 'FDA approval letter', url: 'https://www.fda.gov/media/193444/download' },
    ],
  },
  {
    title: 'Six-monthly HIV prevention with lenacapavir',
    field: 'Infectious disease',
    status: 'Approved use',
    date: 'Approved June 2025',
    summary: 'The US FDA approved lenacapavir for pre-exposure prophylaxis in eligible adults and adolescents, with continuation injections every six months after initiation.',
    caution: 'A negative HIV test is essential before starting and before each injection. This is prevention, not stand-alone HIV treatment.',
    sources: [
      { label: 'FDA approval letter', url: 'https://www.accessdata.fda.gov/drugsatfda_docs/appletter/2025/220020Orig1s000ltr.pdf' },
      { label: 'FDA prescribing information', url: 'https://www.accessdata.fda.gov/drugsatfda_docs/label/2025/220020s000lbl.pdf' },
    ],
  },
  {
    title: 'Continuous glucose monitoring beyond prescriptions',
    field: 'Connected devices',
    status: 'Cleared device',
    date: 'Updated June 2026',
    summary: 'Over-the-counter continuous glucose monitors are widening access for some people who do not use insulin, making glucose patterns easier to observe without a prescription in the United States.',
    caution: 'These devices are not suitable for every person or every clinical decision. Treatment should not be changed solely from consumer-device output.',
    sources: [
      { label: 'FDA: first OTC CGM', url: 'https://www.fda.gov/news-events/press-announcements/fda-clears-first-over-counter-continuous-glucose-monitor' },
      { label: 'FDA: expanded clearance', url: 'https://www.fda.gov/news-events/press-announcements/fda-clears-first-over-counter-continuous-glucose-monitor-children' },
    ],
  },
  {
    title: 'Personalised mRNA vaccines after pancreatic-cancer surgery',
    field: 'Cancer research',
    status: 'Investigational',
    date: 'Phase 2 ongoing',
    summary: 'A small early study built an mRNA vaccine around each person’s tumour. Long-lived immune responses in some participants have supported a larger randomised study.',
    caution: 'The initial cohort was tiny and non-randomised, so it cannot establish clinical benefit. This approach is not an approved treatment.',
    sources: [
      { label: 'Nature: phase 1 follow-up', url: 'https://www.nature.com/articles/s41586-024-08508-4' },
      { label: 'ClinicalTrials.gov', url: 'https://clinicaltrials.gov/study/NCT05968326' },
    ],
  },
  {
    title: 'Stem-cell-derived islet cells for type 1 diabetes',
    field: 'Regenerative medicine',
    status: 'Investigational',
    date: 'Early results June 2025',
    summary: 'Early-phase findings suggest stem-cell-derived islet cells may restore insulin production in some participants. Larger studies are examining durability and safety.',
    caution: 'The report was small, had no control group, required chronic immunosuppression and included serious adverse events. It is not approved treatment.',
    sources: [
      { label: 'NEJM: phase 1–2 study', url: 'https://www.nejm.org/doi/10.1056/NEJMoa2506549' },
      { label: 'ClinicalTrials.gov', url: 'https://clinicaltrials.gov/study/NCT04786262' },
    ],
  },
  {
    title: 'CAR T cells as a possible immune reset',
    field: 'Autoimmune disease',
    status: 'Investigational',
    date: 'Formal trials underway',
    summary: 'A small case series explored CD19-directed CAR T cells in severe, treatment-resistant lupus, inflammatory myositis and systemic sclerosis, with encouraging remissions.',
    caution: 'The evidence is early and uncontrolled. Treatment involves chemotherapy and potentially serious immune and infection risks; it is not approved for autoimmune disease.',
    sources: [
      { label: 'NEJM: case series', url: 'https://www.nejm.org/doi/10.1056/NEJMoa2308917' },
      { label: 'ClinicalTrials.gov', url: 'https://clinicaltrials.gov/study/NCT05869955' },
    ],
  },
]
